import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { BarChart3, RefreshCcw } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { PageHeader, ResizableSidebar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { useAuthStore } from "@/features/auth";
import {
  OrganizationTree,
  useOrganizationStore,
} from "@/features/organization";
import { useEsgDashboardStore } from "../hooks";
import ReportSelector from "../components/dashboard/ReportSelector";
import YearSelector from "../components/dashboard/YearSelector";
import KpiOverviewCards from "../components/dashboard/KpiOverviewCards";
import PerformanceCharts from "../components/dashboard/PerformanceCharts";
import CategoryProgress from "../components/dashboard/CategoryProgress";
import BenchmarkingChart from "../components/dashboard/BenchmarkingChart";
import CompletionTracker from "../components/dashboard/CompletionTracker";
import MetricsModal from "../components/dashboard/MetricsModal";
import EsgDashboardSkeleton from "../components/skeleton/EsgDashboardSkeleton";

const EsgDashboardPage = observer(() => {
  const { t } = useTran();
  const {
    refreshDashboard,
    refreshing,
    loading,
    ensureData,
    consolidatedMode,
    selectedReportId,
    setSelectedOrgId,
  } = useEsgDashboardStore();
  const { user } = useAuthStore();
  const {
    fetchHierarchy,
    selectedId,
    tree,
    loading: orgLoading,
  } = useOrganizationStore();
  const loginOrgId =
    user?.organizationUnitId ||
    user?.organizationUnit?.id ||
    user?.organization?.id ||
    null;

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  useEffect(() => {
    if (orgLoading || tree.length) return;
    fetchHierarchy({ selectId: loginOrgId }).catch(() => {});
  }, [fetchHierarchy, loginOrgId, tree.length]);

  useEffect(() => {
    if (!selectedId) return;
    setSelectedOrgId(selectedId);
  }, [selectedId, setSelectedOrgId]);

  const showDashboard = Boolean(selectedReportId || consolidatedMode);
  const showSkeleton = useDelayedFlag(loading);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title={t("ESG KPI Dashboard")}
          description={t(
            "Monitor performance, track progress, and compare against peer benchmarks.",
          )}
          icon={BarChart3}
        />
        <Button
          variant="outline"
          onClick={refreshDashboard}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCcw
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          {t("Refresh")}
        </Button>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <ResizableSidebar
          storageKey="nxstart_esg_tree_width"
          handleTitle={t("Drag to resize")}
        >
          <OrganizationTree />
        </ResizableSidebar>
        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ReportSelector />
            <YearSelector />
          </div>

          {showDashboard ? (
            <>
              <KpiOverviewCards />

              <div className="space-y-3">
                <p className="text-lg font-semibold text-foreground">
                  {t("Performance Analysis")}
                </p>
                <PerformanceCharts />
              </div>

              <div className="space-y-3">
                <p className="text-lg font-semibold text-foreground">
                  {t("Progress by Category")}
                </p>
                <CategoryProgress />
              </div>

              {/* <div className="space-y-3"> */}
              {/*   <p className="text-lg font-semibold text-foreground"> */}
              {/*     {t("Industry Benchmarking")} */}
              {/*   </p> */}
              {/*   <BenchmarkingChart /> */}
              {/* </div> */}

              <div className="space-y-3">
                <p className="text-lg font-semibold text-foreground">
                  {t("Framework Completion")}
                </p>
                <CompletionTracker />
              </div>
            </>
          ) : showSkeleton ? (
            <EsgDashboardSkeleton />
          ) : (
            <Card className="border border-dashed border-border">
              <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground" />
                <p className="text-base font-semibold text-foreground">
                  {t("Select a report to view the dashboard")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "Choose a report from the dropdown to view performance metrics and analytics.",
                  )}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {!loading ? <MetricsModal /> : null}
    </div>
  );
});

export default EsgDashboardPage;
